<!-- @migration-task Error while migrating Svelte code: Event attribute must be a JavaScript expression, not a string
https://svelte.dev/e/attribute_invalid_event_handler -->
<script lang="ts">
  // (c) Cognisoft Aps, 2022, for Svelte 5: 2026
  // spell noun from picture
  // target last picture in step 4
  // first letter is pre-set-in
  // letter animates to target position
  // flyes back if error

  import { flip } from "svelte/animate";
  import { crossfade } from "svelte/transition";
  import { onDestroy } from "svelte";

  import { Pages, DaDK, Path, Extension, State } from "../enums";
  import { HelpLevel } from "../classes";
  import {
    afterCorrectWaitSecs,
    firstWaitSecs,
    animationBugWait,
  } from "../constants";

  import { Icons } from "../utilities/icons";
  import { shuffleArray } from "../utilities/shuffleArray";
  import { playSoundWithAudioTag } from "../utilities/playSound";
  import { playCorrectRandomWithAudioTag } from "../utilities/playCorrectRandom";
  import { getTotalNumberOfTasks } from "../utilities/getTotalNumberOfTasks";
  import { setCompletedBackgroundColor } from "../utilities/setCompletedBackgroundColor";
  import { logger } from "../utilities/logger";

  import { studentUserData } from "../stores/user-store.svelte";
  import { route } from "../stores/route.svelte";
  import {
    currentSentenceNumberForSteps,
    targetsForSteps,
  } from "../stores/targets-step567.svelte"; // last picture shown in step 4
  import { targetVerbAndObjects } from "../globals/targets";
  import { progress } from "../stores/progress.svelte";
  import { helpCount } from "../stores/helpCount.svelte";
  import { result } from "../stores/result.svelte";
  import { tasksCompleted } from "../stores/tasksCompleted.svelte";

  import ExitModal from "../modals/ExitModal.svelte";

  import RoundButton from "../components/RoundButton.svelte";
  import ProgressBar from "../components/ProgressBar.svelte";
  import PauseMessage from "../components/PauseMessage.svelte";

  // PROPS --------------------------------------------------

  // one audiotag in App.svelte is used on all pages in order to have
  //   sound played even before mouse click

  let { audioTag }: { audioTag: HTMLAudioElement } = $props();

  // TYPES --------------------------------------------------

  // has to be object to make key for svelte animation
  //  because letter may be repeated, and repeated strings not allowed
  type Letter = {
    letter: string;
  };

  type Letters = Array<Letter>;

  // CONSTANTS --------------------------------------------

  const [send, receive] = crossfade({});

  const numberOfHelpLevels = 4;

  // STATE ------------------------------------------------

  let letters: Letters = $state([]); // if letter is space it is hidden from shuffled letters to chose from
  let shuffledLetters: Letters = $state([]); // letters to choose from
  let letterOrder: Array<number> = $state([]); // shuffled letter order
  let receivedLetters: Letters = $state([]); // received - for anmation
  let insertedLetters: Array<boolean> = $state([]); // keep track of inserted
  let letterWiggle: Array<boolean> = $state([]); // wiggle letter if move after error to correct postion
  let numberCorrect: number = $state(1); // keep track when finished, start at 1 as first letter is given as help from beginning
  // for help:
  let shuffledLettersHelpPosition: number = $state(0);
  let receivedLettersHelpPosition = $state(0);

  let selectedChoiceLetter: number = $state(-1); // -1 = unselected, 0 or larger = index of selected choice letter

  let currentState: State = $state(State.WaitToStart); // currentState for task solution for each item/picture
  let oldState: State = $state(State.WaitToStart); // keep old currentState during pause and exitModal

  let repeatButtonTimer: ReturnType<typeof setTimeout>;
  let helpTimer: ReturnType<typeof setTimeout>;

  let showKeyHelp = $state(false);

  // help both after a set time, requested and after error
  let helpLevel = $state(new HelpLevel(numberOfHelpLevels));

  let allowRepetition = $state(false);
  let requestedHelpPaused = $state(false); // help not active for some seconds after activated last time

  // same error 3 times in a row, then move letter to correct position
  let lastErrorPosition = $state(0); // we put first letter ind, so error can never be at pos 0
  let lastErrorLetter = $state("");
  let numberOfSameError = $state(0);

  // for results:
  let gotHelpForItem = $state(false);
  let hasErrorForItem = $state(false);

  // FOR RESEARCH ONLY --------------------------------------------
  // save results for each response, push to array in results.store
  // let presentTime: Date;
  // let numberOfTimedHelp = 0;
  // let numberOfRequestedHelp = 0;
  // let numberOfRequestedRepetitions = 0;
  // let attempt = 1;
  // let responseDetails: Array<ResponseDetail> = [];

  // keep track of pause time
  let pauseStartTime: number;
  let pauseEndTime: number;

  // EFFECTS --------------------------------------------

  $effect(() => {
    logger("Step5 currentState:", currentState);
  });
  $effect(() => {
    logger("Step5 helpLevel:", helpLevel.level);
  });

  // FUNCTIONS --------------------------------------------

  const countHelp = () => {
    switch (currentSentenceNumberForSteps.value) {
      case 0:
        helpCount.value.spelling.item51++;
        break;
      case 1:
        helpCount.value.spelling.item52++;
        break;
      case 2:
        helpCount.value.spelling.item53++;
        break;
      case 3:
        helpCount.value.spelling.item54++;
        break;
    }
  };

  // adjust help count by number of letters in word to get a mean
  const adjustHelpCount = () => {
    switch (currentSentenceNumberForSteps.value) {
      case 0:
        helpCount.value.spelling.item51 =
          helpCount.value.spelling.item51 / letters.length;
        break;
      case 1:
        helpCount.value.spelling.item52 =
          helpCount.value.spelling.item52 / letters.length;
        break;
      case 2:
        helpCount.value.spelling.item53 =
          helpCount.value.spelling.item53 / letters.length;
        break;
      case 3:
        helpCount.value.spelling.item54 =
          helpCount.value.spelling.item54 / letters.length;
        break;
    }
  };

  // showing help according to evel hierarky

  // show repeat button a defined time after showin task

  const startRepeatButtonTimer = () => {
    if (currentState === State.WaitAfterEnd) return;
    allowRepetition = false;
    repeatButtonTimer = setTimeout(() => {
      allowRepetition = true;
    }, studentUserData.value.settings.repeatDelaySecs * 1000);
  };

  // showing help according to 2 level hierarkies (timed and requested help) ---

  const startHelpTimer = () => {
    if (currentState === State.WaitAfterEnd) return;
    currentState = State.WaitForResponse;
    requestedHelpPaused = false;
    helpTimer = setTimeout(() => {
      // numberOfTimedHelp++; // FOR RESEARCH ONLY
      giveHelp("timeOut");
    }, studentUserData.value.settings.helpDelaySecs * 1000);
  };

  const startHelpTimerAndWaitForResponse = () => {
    if (currentState === State.WaitAfterEnd) return;
    currentState = State.WaitForResponse;
    startHelpTimer();
  };

  // sound both repeated with speaker button and at 2 first help stages
  const repeatObjectHelp = (): void => {
    if (currentState === State.WaitAfterEnd) return;
    helpLevel.setNextLevel();
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object,
      startHelpTimerAndWaitForResponse,
    );
  };

  const findFirstEmptyReceivingPosition = (): number => {
    // empty positions contains a number
    for (let i = 0; i < receivedLetters.length; i++) {
      if (
        !targetVerbAndObjects.value[
          targetsForSteps.value[currentSentenceNumberForSteps.value]
        ].object.includes(receivedLetters[i].letter)
      ) {
        return i;
      }
    }
    console.error(
      "step 5: error findFirstEmptyReceivingPosition, receivedLetters: ",
      JSON.stringify(receivedLetters),
    );
    return 0;
  };

  const showCorrectLetterHelp = () => {
    receivedLettersHelpPosition = findFirstEmptyReceivingPosition();
    const currentLetter = targetVerbAndObjects.value[
      targetsForSteps.value[currentSentenceNumberForSteps.value]
    ].object.charAt(receivedLettersHelpPosition);
    shuffledLettersHelpPosition = shuffledLetters.findIndex((letter) => {
      return letter.letter === currentLetter;
    });
    showKeyHelp = true;
    helpLevel.setNextLevel();
    setTimeout(() => {
      requestedHelpPaused = false;
    }, 1000);
    startHelpTimerAndWaitForResponse();
  };

  const showCorrectAnswerHelp = () => {
    showKeyHelp = false;
    helpLevel.setNextLevel();
    const currentLetter = targetVerbAndObjects.value[
      targetsForSteps.value[currentSentenceNumberForSteps.value]
    ].object.charAt(receivedLettersHelpPosition);
    const shuffledLettersPosition = shuffledLetters.findIndex((letter) => {
      return letter.letter === currentLetter;
    });
    let tempLetter: Letter = shuffledLetters[shuffledLettersPosition];
    shuffledLetters.splice(shuffledLettersPosition, 1, { letter: " " }); // " " signals to be hidden
    receivedLetters.splice(receivedLettersHelpPosition, 1, tempLetter); // insert in receivedletters
    receivedLetters = receivedLetters; // so svelte will update
    shuffledLetters = shuffledLetters;
    insertedLetters.splice(receivedLettersHelpPosition, 1, true); // tell position occupied
    insertedLetters = insertedLetters;
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
    increaseCorrectAndCheckForFinished();
  };

  // give requested help after clicking help button or after timed help according to help level
  const giveHelp = (helpType: string) => {
    if (currentState !== State.WaitForResponse) {
      return;
    }
    currentState = State.GivingHelp;
    logger("Step5 giveHelp - " + helpType);
    countHelp();
    gotHelpForItem = true;
    requestedHelpPaused = true;
    clearTimeout(helpTimer);
    switch (helpLevel.level) {
      case 1:
      case 2:
        repeatObjectHelp();
        break;
      case 3:
        showCorrectLetterHelp();
        break;
      case 4:
        showCorrectAnswerHelp();
        break;
      default:
        console.error(
          "step 5: error in giveHelp() switch, helpLevel: ",
          helpLevel.level,
        );
    }
  };

  const requestedHelp = () => {
    if (currentState !== State.WaitForResponse) {
      return;
    }
    clearTimeout(helpTimer);
    currentState = State.RepeatingSound;
    logger("Step5 requestedHelp");
    countHelp();
    // numberOfRequestedHelp++; // FOR RESEARCH ONLY
    requestedHelpPaused = true;
    playSoundWithAudioTag(audioTag, DaDK.Step5Help, startHelpTimer);
  };

  // start task logic

  // play word as first thing after intro
  const playTargetWord = (): void => {
    currentState = State.PresentTask;
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object,
      waitToBegin,
    );
  };

  // wait to start
  const waitToBegin = (): void => {
    currentState = State.WaitToStart;
    setTimeout(() => {
      moveFirstLetter();
    }, firstWaitSecs);
  };

  // insert first letter as help at start of task
  const moveFirstLetter = (): void => {
    let letter: Letter;
    // find the target letter, put it ind and prepare svelte animation arrays
    for (let i = 0; i < shuffledLetters.length; i++) {
      if (
        shuffledLetters[i].letter ===
        targetVerbAndObjects.value[
          targetsForSteps.value[currentSentenceNumberForSteps.value]
        ].object.charAt(0)
      ) {
        letter = shuffledLetters.splice(i, 1, { letter: " " })[0]; // " " signals to be hidden
        receivedLetters.splice(0, 1, letter); // insert in received letters
        break;
      }
    }
    // register that first letter is inserted
    insertedLetters.splice(0, 1, true);
    insertedLetters = [...insertedLetters];
    receivedLetters = [...receivedLetters];
    shuffledLetters = [...shuffledLetters];
    currentState = State.WaitForResponse;
    startHelpTimerAndWaitForResponse();
    // initializeResultVariables();  // FOR RESEARCH ONLY
    startRepeatButtonTimer();
  };

  // toggle selected status for choice letter
  const choiceLetterClicked = (index: number): void => {
    if (selectedChoiceLetter === index) {
      selectedChoiceLetter = -1;
    } else {
      selectedChoiceLetter = index;
    }
  };

  const increaseCorrectAndCheckForFinished = () => {
    numberCorrect++;
    selectedChoiceLetter = -1; // signals no choice
    // reset count for identical errors:
    resetRepetitionErrors();
    // if all letters correctly inserted, end:
    if (numberCorrect >= letters.length) {
      progress.value += 1;
      progress;
      currentState = State.Correct;
      if (!gotHelpForItem && !hasErrorForItem) {
        result.value.spellingCorrectFirstTry =
          result.value.spellingCorrectFirstTry + 1;
      }
      // adjust help count by number of letters in word
      adjustHelpCount();
      playCorrectRandomWithAudioTag(audioTag, afterCorrect);
    } else {
      setTimeout(() => {
        requestedHelpPaused = false;
      }, 1000);
      startHelpTimerAndWaitForResponse();
    }
  };

  // button click event handlers ----------

  // inset choice letter in target word if letter position not already filled
  //   if correct: stays in position, if error should jump back to choice letters
  const targetClicked = (index: number): void => {
    if (selectedChoiceLetter >= 0 && index > 0 && !insertedLetters[index]) {
      let tempLetter: Letter = shuffledLetters[selectedChoiceLetter];
      shuffledLetters.splice(selectedChoiceLetter, 1, { letter: " " }); // " " signals to be hidden
      receivedLetters.splice(index, 1, tempLetter); // insert in receivedletters
      receivedLetters = receivedLetters; // so svelte will update
      shuffledLetters = shuffledLetters;
      insertedLetters.splice(index, 1, true); // tell position occupied
      insertedLetters = insertedLetters;
      // check if correct
      if (tempLetter.letter !== letters[index].letter) {
        // error:
        // saveResultVariablesWithCorrect(false);  // FOR RESEARCH ONLY
        // attempt++;  // FOR RESEARCH ONLY
        currentState = State.Error;
        setTimeout(() => {
          afterError(index, tempLetter);
        }, firstWaitSecs);
      } else {
        // correct:
        // saveResultVariablesWithCorrect(true);  // FOR RESEARCH ONLY
        // attempt = 1;  // FOR RESEARCH ONLY
        showKeyHelp = false;
        clearTimeout(repeatButtonTimer);
        clearTimeout(helpTimer);
        helpLevel.reset();
        increaseCorrectAndCheckForFinished();
      }
    }
  };

  // correct -----

  // after all letters correct, go on
  const afterCorrect = (): void => {
    setCompletedBackgroundColor(true);
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object,
      gotoNextTask,
    );
  };

  const gotoNextTask = () => {
    currentState = State.WaitAfterEnd;
    tasksCompleted.value = tasksCompleted.value + 1;
    // result.value.responseDetails.push(...responseDetails);
    result.value.lastStepCompleted = 5;
    setTimeout(() => {
      if (studentUserData.value.settings.simpleSpelling) {
        if (currentSentenceNumberForSteps.value < 3) {
          currentSentenceNumberForSteps.value =
            currentSentenceNumberForSteps.value + 1;
          initializeTask();
        } else {
          route.current = Pages.Step8;
        }
      } else {
        route.current = Pages.Step6;
      }
    }, afterCorrectWaitSecs);
  };

  // error -----

  const resetRepetitionErrors = () => {
    numberOfSameError = 0;
    lastErrorLetter = "";
    lastErrorPosition = 0;
  };

  const afterError = (index: number, chosenLetter: Letter): void => {
    clearTimeout(helpTimer);
    // have to move letter by manipulating arrays
    hasErrorForItem = true;
    insertedLetters.splice(index, 1, false);
    receivedLetters.splice(index, 1, { letter: index.toString() }); // number signals to show with receive border
    // count number of same error
    if (
      chosenLetter.letter === lastErrorLetter &&
      index === lastErrorPosition
    ) {
      numberOfSameError++;
    }
    if (numberOfSameError > 1) {
      // move letter to correct position
      resetRepetitionErrors();
      helpLevel.reset();
      currentState = State.WaitForResponse;
      selectedChoiceLetter = -1;
      playSoundWithAudioTag(audioTag, DaDK.Step5ObsMove, null);
      // move letter to correct position
      // time out here is HACK !!! for flip animation bug in svelte ***:
      setTimeout(() => {
        moveToCorrectPosition(chosenLetter);
        startHelpTimer();
      }, animationBugWait);
    } else {
      lastErrorLetter = chosenLetter.letter;
      lastErrorPosition = index;
      // move letter back
      // time out here is HACK !!! for flip animation bug in svelte ***:
      setTimeout(() => {
        afterError2(index, chosenLetter);
      }, animationBugWait);
    }
  };

  const afterError2 = (index: number, chosenLetter: Letter): void => {
    // move letter back after error
    shuffledLetters.splice(selectedChoiceLetter, 1, chosenLetter);
    shuffledLetters = shuffledLetters;
    selectedChoiceLetter = -1;
    currentState = State.WaitForResponse;
    giveHelp("error");
  };

  const findCorrectPosition = (letter: Letter): number => {
    // find correct position even though letter may be in more positions and one may already be found
    const tempObject =
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object;
    for (let i = 0; i < tempObject.length; i++) {
      if (
        tempObject.charAt(i) === letter.letter &&
        receivedLetters[i].letter !== letter.letter
      ) {
        return i;
      }
    }
    console.error(
      "step 5: findCorrectPosition, correctPosition not found, letter: ",
      JSON.stringify(letter.letter),
    );
    return -1;
  };

  const moveToCorrectPosition = (chosenLetter: Letter): void => {
    numberCorrect++;
    const correctPosition = findCorrectPosition(chosenLetter);
    receivedLetters.splice(correctPosition, 1, chosenLetter);
    insertedLetters.splice(correctPosition, 1, true);
    receivedLetters = receivedLetters;
    insertedLetters = insertedLetters;
    letterWiggle[correctPosition] = true;
    letterWiggle = letterWiggle;
    receivedLettersHelpPosition = -1; // stop help highlight
    setTimeout(() => {
      stopWiggle(correctPosition);
    }, 1000);
  };

  const stopWiggle = (index: number) => {
    letterWiggle[index] = false;
    letterWiggle = letterWiggle;
    startHelpTimer();
  };

  // when repeat button is pressed
  const repeatSound = () => {
    if (currentState !== State.WaitForResponse) {
      return;
    }
    currentState = State.RepeatingSound;
    countHelp();
    // numberOfRequestedRepetitions++;  // FOR RESEARCH ONLY
    clearTimeout(helpTimer);
    requestedHelpPaused = true;
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object,
      startHelpTimer,
    );
  };

  // DRAG and DROP -----------------------------------------

  const dragStart = (index: number): void => {
    shuffledLettersHelpPosition = -1; // stop help highligt on drag
    selectedChoiceLetter = index;
  };

  const drop = (index: number): void => {
    targetClicked(index);
  };

  // pause --------

  const pauseClicked = (): void => {
    if (currentState === State.Paused) {
      pauseEndTime = Date.now();
      result.value.pauseElapsed =
        result.value.pauseElapsed + Math.abs(pauseEndTime - pauseStartTime);
      currentState = oldState;
      allowRepetition = true;
      startHelpTimer();
    } else {
      currentState = State.Paused;
      pauseStartTime = Date.now();
      // stopSoundWithAudioTag(audioTag);
      clearTimeout(repeatButtonTimer);
      clearTimeout(helpTimer);
      oldState = currentState;
    }
  };

  // exit task prematurely after stop button clicked

  // stop button: show yes-no modal
  const exitButtonClicked = () => {
    currentState = State.ExitModalShown;
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
  };

  // exit task (modal event "yes")
  const exitTask = () => {
    // stopSoundWithAudioTag(audioTag);
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
    result.value.wasInterrupted = true;
    route.current = Pages.EndResults;
  };

  // dismiss exit modal (event "no")
  const dismissExitModal = () => {
    allowRepetition = true;
    startHelpTimer();
  };

  // LIFE CYCLE ---------------------------------------------------

  onDestroy(() => {
    clearTimeout(helpTimer);
    clearTimeout(repeatButtonTimer);
  });

  // INITIALIZE --------------------------------------------

  // FOR RESEARCH ONLY
  // const initializeResultVariables = () => {
  //   presentTime = new Date();
  //   numberOfTimedHelp = 0;
  //   numberOfRequestedHelp = 0;
  //   numberOfRequestedRepetitions = 0;
  // };

  // FOR RESEARCH ONLY
  // // save result details for a single item, used for cvs results
  // // *** used for research disabled in current production
  // const saveResultVariablesWithCorrect = (correct: boolean) => {
  // // for research - disabled in current production
  // responseDetails.push({
  //   step: 5,
  //   attempt: attempt,
  //   presentTime: presentTime,
  //   responseTime: new Date(),
  //   targetItem:
  //     targetVerbAndObjects.value[targetsForSteps.value[currentSentenceNumberForSteps.value]]
  //       .object,
  //   correct: correct,
  //   numberOfTimedHelp: numberOfTimedHelp,
  //   numberOfRequestedHelp: numberOfRequestedHelp,
  //   numberOfRequestedRepetitions: numberOfRequestedRepetitions,
  // });
  // };

  function initializeTask() {
    letters = []; // if letter is space it is hidden from shuffled letters to chose from
    shuffledLetters = []; // letters to choose from
    letterOrder = []; // shuffled letter order
    receivedLetters = []; // received - for animation
    insertedLetters = []; // keep track of inserted
    letterWiggle = []; // wiggle letter if move after error to correct postion
    numberCorrect = 1; // keep track when finished, start at 1 as first letter is given as help from beginning
    // for help:
    shuffledLettersHelpPosition = 0;
    receivedLettersHelpPosition = 0;
    lastErrorPosition = 0; // we put first letter ind, so error can never be at pos 0
    lastErrorLetter = "";
    numberOfSameError = 0;

    // for results:
    gotHelpForItem = false;
    hasErrorForItem = false;

    // FOR RESEARCH ONLY
    // save results for each response, push to array in results.store
    // numberOfTimedHelp = 0;
    // numberOfRequestedHelp = 0;
    // numberOfRequestedRepetitions = 0;
    // attempt = 1;

    selectedChoiceLetter = -1; // -1

    // push a number from 0 and increasing for each letter into array
    for (
      let i = 0;
      i <
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object.length;
      i++
    ) {
      letterOrder.push(i);
    }
    // console.error("step 5: letterOrder array unshuffled: ", letterOrder); // *** DEBUG ***
    // shuffle letter order arry randomly
    shuffleArray(letterOrder);
    // console.error("step 5: letterOrder array shuffled: ", letterOrder); // *** DEBUG ***
    // shuffle letter order array randomly make:
    // 1) array of letters in word: letters
    // 2) make array of shuffled letters for user to choose from when spelling (hide when used)
    // 3) make array of received letters = letters in word (show when chosen correctly)
    // 4) make boolean array to tell if each position has got a letter: insertedLetters
    for (let i = 0; i < letterOrder.length; i++) {
      letters.push({
        letter:
          targetVerbAndObjects.value[
            targetsForSteps.value[currentSentenceNumberForSteps.value]
          ].object.charAt(i),
      });
      shuffledLetters.push({
        letter: targetVerbAndObjects.value[
          targetsForSteps.value[currentSentenceNumberForSteps.value]
        ].object.charAt(letterOrder[i]),
      });
      receivedLetters.push({ letter: i.toString() }); // number until char is inserted
      insertedLetters.push(false);
      letterWiggle.push(false);
    }
    // for svelte responsive update:
    shuffledLetters = [...shuffledLetters];
    receivedLetters = [...receivedLetters];
    insertedLetters = [...insertedLetters];

    currentState = State.PlayIntro;
    playSoundWithAudioTag(audioTag, DaDK.Step5Intro, playTargetWord);
  }

  setCompletedBackgroundColor(false);
  initializeTask();
</script>

<!-- HTML ===================================================== -->

<main data-page="step5">
  <!-- data for Playwright tests -->
  <div
    data-testid="state"
    data-state={currentState}
    style="display: none;"
  ></div>
  <div
    data-testid="count"
    data-count={targetVerbAndObjects.value[
      targetsForSteps.value[currentSentenceNumberForSteps.value]
    ].object.length}
    style="display: none;"
  ></div>
  <!-- top panel with help and repeat buttons -->
  <div class="top-panel">
    <div>
      <span class="stop-button">
        <RoundButton
          disabled={currentState !== State.WaitForResponse}
          onclick={exitButtonClicked}
          icon={Icons.TrafficLightRed}
        />
      </span>
      <RoundButton
        disabled={currentState !== State.WaitForResponse &&
          currentState !== State.Paused}
        onclick={pauseClicked}
        icon={currentState === State.Paused ? Icons.Play : Icons.Pause}
      />
    </div>
    <div>
      <RoundButton
        disabled={currentState !== State.WaitForResponse || !allowRepetition}
        onclick={repeatSound}
        icon={Icons.VolumeRegular}
      />
      <span class="help-button">
        <RoundButton
          disabled={currentState !== State.WaitForResponse ||
            requestedHelpPaused}
          onclick={requestedHelp}
          icon={Icons.QuestionSolid}
        />
      </span>
    </div>
  </div>
  <!-- image showing target -->
  <div
    class="image"
    style="background-image: url('{encodeURI(
      Path.ImgBg +
        targetVerbAndObjects.value +
        targetsForSteps.value[currentSentenceNumberForSteps.value],
    )}
      .image}{Extension.ImgBg}');
      background-image: image-set('{encodeURI(
      Path.ImgBg +
        targetVerbAndObjects.value +
        targetsForSteps.value[currentSentenceNumberForSteps.value],
    )}
      .image}{Extension.WebpBg}'); 
       background-size: cover;"
  >
    <picture>
      <source
        type="image/webp"
        srcset={encodeURI(
          Path.Img +
            targetVerbAndObjects.value[
              targetsForSteps.value[currentSentenceNumberForSteps.value]
            ].image +
            Extension.Webp,
        )}
        draggable={false}
      />
      <img
        draggable={false}
        src={encodeURI(
          Path.Img +
            targetVerbAndObjects.value[
              targetsForSteps.value[currentSentenceNumberForSteps.value]
            ].image +
            Extension.Png,
        )}
        alt=""
      />
    </picture>
  </div>
  <!-- the spelled word under picture -->
  <div class="spelling">
    {#each receivedLetters as letter, index (letter)}
      <button
        data-receive-index={index}
        class:key-help={showKeyHelp &&
          receivedLettersHelpPosition === index &&
          currentState === State.WaitForResponse}
        class:img-wiggle={letterWiggle[index]}
        class:gray-text={currentState !== State.WaitForResponse}
        ondragover={(event) => event.preventDefault()}
        ondrop={() => drop(index)}
        animate:flip
        in:receive|globale={{ key: letter }}
        out:send|global={{ key: letter }}
        disabled={currentState !== State.WaitForResponse}
        class="receive-letter receive-letter-{index}"
        class:receive-letter-content={insertedLetters[index]}
        onclick={() => targetClicked(index)}
      >
        {#if insertedLetters[index]}
          {letter.letter}
        {/if}
      </button>
    {/each}
  </div>
  <!-- panel to choose letters to spell with from -->
  <div class="right-panel">
    {#each shuffledLetters as letter, index (letter)}
      <button
        data-select-index={index}
        class:key-help={showKeyHelp &&
          shuffledLettersHelpPosition === index &&
          currentState === State.WaitForResponse}
        class:gray-text={currentState !== State.WaitForResponse}
        draggable={currentState === State.WaitForResponse}
        ondragstart={() => dragStart(index)}
        animate:flip
        in:receive|global={{ key: letter }}
        out:send|global={{ key: letter }}
        disabled={currentState !== State.WaitForResponse}
        class={"letter letter-" + letterOrder[index]}
        class:letterHidden={letter.letter === " "}
        class:letter-selected={selectedChoiceLetter === index}
        onclick={() => choiceLetterClicked(index)}
      >
        {letter.letter}
      </button>
    {/each}
  </div>
  <div class="progress">
    <ProgressBar
      max={getTotalNumberOfTasks(studentUserData.value.currentLevel)}
      position={progress.value}
    />
  </div>

  <!-- MODALS -------------------------------------- -->
  {#if currentState === State.Paused}
    <PauseMessage pauseMessageClicked={pauseClicked} />
  {/if}

  {#if currentState === State.ExitModalShown}
    <ExitModal yes={exitTask} no={dismissExitModal} />
  {/if}
</main>

<!-- CSS ====================================================== -->
<style>
  main {
    margin-left: auto;
    margin-right: auto;
    width: 120vh;
    height: calc(var(--vh, 1vh) * 100);
    display: grid;
    grid-template-columns: 74% 24%;
    grid-template-rows: 14% 58% 22%;
    grid-template-areas:
      "top top"
      "img letters"
      "word letters"
      "prog prog";
    gap: 2%;
    padding: 3%;
    padding-top: 2%;
    padding-left: 2%;
  }
  @media screen and (max-aspect-ratio: 6/5) {
    main {
      width: 100vw;
    }
  }
  .top-panel {
    grid-area: top;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .image {
    background: white;
    border: solid 1px black;
    grid-area: img;
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: bottom;
  }
  .right-panel {
    border: solid 2px var(--button-border-color);
    grid-area: letters;
    position: relative;
  }
  .spelling {
    grid-area: word;
    border: solid 2px var(--button-border-color);
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
  }
  .progress {
    grid-area: prog;
    width: 100%;
  }
  .letter {
    position: absolute;
    width: 8vmin;
    height: 10vmin;
    font-size: 8vmin;
    text-transform: uppercase;
    border: solid 1px black;
    background: white;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0.5vmin;
    cursor: pointer;
    user-select: none;
  }
  .gray-text {
    color: darkgray;
  }
  .letter-0 {
    top: 50%;
    left: 2%;
  }
  .letter-1 {
    top: 48%;
    left: 51%;
  }
  .letter-2 {
    top: 26%;
    left: 5%;
  }
  .letter-3 {
    top: 65%;
    left: 55%;
  }
  .letter-4 {
    top: 10%;
    left: 10%;
  }
  .letter-5 {
    top: 5%;
    left: 51%;
  }
  .letter-6 {
    top: 67%;
    left: 8%;
  }
  .letter-7 {
    top: 32%;
    left: 44%;
  }
  .letter-8 {
    top: 85%;
    left: 40%;
  }
  .letter-9 {
    top: 83%;
    left: 2%;
  }
  .letter-selected {
    border: solid 6px var(--button-border-selected);
    background: lightyellow;
  }
  .letter:hover {
    border: solid 6px var(--button-border-active);
  }
  .letterHidden {
    visibility: hidden;
  }
  .receive-letter {
    width: 10vmin;
    height: 10vmin;
    font-size: 8vmin;
    text-transform: uppercase;
    border: dashed 5px var(--button-active-color);
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 1vmin;
    cursor: pointer;
    user-select: none;
  }
  .receive-letter-content {
    border: solid 1px black;
    background: white;
    cursor: default;
  }
  .key-help {
    background: var(--key-help-background-color);
  }
</style>
